'use client'

import React, { FC, ReactElement } from 'react'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { formatUsdPer } from '@/utils/format-usd'
import { NETWORK, API_URL } from '@/utils/constants'
import classNames from 'classnames'
import { Input } from '@/components/ui/input'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import useWallet from '@/utils/hooks/useWallet'
import Outpoint from '@/utils/outpoint'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import isFungible from '@/utils/is-fungible'
import featureFlags from '@/utils/feature-flags'
import {
	getInscriptionCollectionSlugs,
	inscriptionBelongsToCollection
} from '@/utils/inscription-collections'

import useSelect from '../../hooks/useSelect'

import Tabs from './tabs'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import Item from './list-item'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { protocolFromSlug, SecurePurchaseUnavailableError } from '@/utils/market/secure-purchase'
import { useSnipeProtection } from '@/utils/market/use-snipe-protection'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'
import {
	describeSecureListingResult,
	listSecureItems,
	LEGACY_LISTABLE_CODES,
	isolateTargetForFailures,
	secureListingFailureMessage,
	trackStandardListingPhase
} from '@/utils/market/secure-listing-client'
import { toastListingSuccess } from '@/utils/market/listing-success-toast'
import { listingNoun } from './listing-noun'
import { isProtectedSigningUnsupported } from '@/utils/market/secure-passthrough'

// Codes meaning snipe protection is not on offer for this attempt (as opposed
// to a real problem with the item); the standard listing still works.
const SNIPE_UNAVAILABLE_CODES = new Set([
	'unsupported_protocol',
	'protocol_extraction_unverified',
	'build3_unavailable',
	'capabilities_fetch_failed',
	'capabilities_error',
	'missing_secure_purchase',
	'signer_quorum_unavailable',
	'invalid_rollout_configuration',
	'listing_not_found',
	'cosigner_unavailable',
	'secure_listing_unavailable',
	'unsupported_wallet'
])

const MARKET_FEE_RATE = 2.7
const MARKET_FEE_MIN = 1000

function calcStep(n: any) {
	var order = Math.floor(Math.log(n) / Math.LN10 + 0.000000001) // because float math sucks like that
	return Math.pow(10, order) * 1e-1
}

/**
 * The UTXO an inscription row sits on, as `txid:vout`, whichever shape the
 * row came in. The wallet API encodes outpoints as 36-byte hex (txid LE +
 * vout LE), so those are decoded; `txid:vout` strings pass through.
 */
function outpointKey(row: any): string | null {
	const value = row?.utxoOutpoint || row?.outpoint?.outpoint || row?.outpoint
	if (typeof value !== 'string') return null
	if (value.includes(':')) return value
	if (/^[0-9a-f]{72}$/i.test(value)) {
		try {
			const { txid, vout } = Outpoint.decode(value)
			return `${txid}:${vout}`
		} catch {
			return null
		}
	}
	return null
}

function listingToastTarget(slug: string, rows: any[]) {
	return {
		slug,
		inscriptionIds: rows
			.map((row) => row?.inscription_id || row?.id || row?.outpoint)
			.filter(Boolean),
		collectionSlugs: rows.flatMap((row) => getInscriptionCollectionSlugs(row))
	}
}

const CollectionDetailList: FC<{
	marketActions?: React.ReactNode
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
	prepared?: { ids: string[]; price: string }
	collectionFilter?: string
}> = (props): ReactElement => {
	const { floorSats, onClose, args, tab, onChangeTab, token, classification, collectionFilter } =
		props

	const { slug } = args
	const { toast } = useToast()
	const currency = isFungible(slug) ? 'sats' : NETWORK
	const step = calcStep(floorSats)

	//
	// Local State
	//

	const [globalPrice, setGlobalPrice] = React.useState(props.prepared?.price || '')
	const [prices, setPrices] = React.useState<Record<string, string | number>>({})
	const [globalSelected, setGlobalSelected] = React.useState(false)

	//
	// Remote Data
	//

	const { exchangeRate } = useExchangeRate()
	const { data: rawData, isLoading } = useTokenOutpoints(token)
	const { data: wallet } = useWallet()
	const isFrozen = slug === 'frozen'

	// How many of the wallet's inscriptions sit on each UTXO, across every
	// collection. A lot that shares its UTXO cannot be listed: protection
	// moves exactly one inscription, and a standard listing would sell all of
	// them for one price. Sellers used to learn this from a rejection and
	// retry it six times; now the lot says so up front.
	const inscriptionsPerOutpoint = React.useMemo(() => {
		const counts = new Map<string, number>()
		for (const inscription of wallet?.inscriptions || []) {
			const key = outpointKey(inscription)
			if (key) counts.set(key, (counts.get(key) || 0) + 1)
		}
		return counts
	}, [wallet])

	const data = React.useMemo(() => {
		if (!rawData || !Array.isArray(rawData)) return rawData
		let rows = rawData
		if (isFrozen && collectionFilter && collectionFilter !== 'all') {
			rows =
				collectionFilter === 'uncategorized'
					? rows.filter((e) => getInscriptionCollectionSlugs(e).length === 0)
					: rows.filter((e) => inscriptionBelongsToCollection(e, collectionFilter))
		}
		if (isFungible(slug)) return rows
		return rows.map((row) => {
			const count = inscriptionsPerOutpoint.get(outpointKey(row) || '') || 0
			return count > 1 ? { ...row, shared_utxo_count: count } : row
		})
	}, [rawData, collectionFilter, isFrozen, slug, inscriptionsPerOutpoint])

	const ids = React.useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return []
		}

		// Select-all and the slider only reach lots that can still be listed.
		return data
			.filter((e) => !e.escrow && e.pending_sale !== true && !e.shared_utxo_count)
			.map((e) => e.outpoint)
	}, [data])
	const {
		selected,
		setSelected,
		setSlider,
		onSelect,
		onSelectAll,
		slider,
		onSliderChange,
		allSelected
	} = useSelect(ids)

	const appliedPreparation = React.useRef<string>('')
	React.useEffect(() => {
		const prepared = props.prepared
		if (!prepared || !prepared.ids.every((id) => ids.includes(id))) return
		const key = JSON.stringify(prepared)
		if (appliedPreparation.current === key) return
		const frame = requestAnimationFrame(() => {
			appliedPreparation.current = key
			setGlobalPrice(prepared.price)
			setPrices((previous) => ({
				...previous,
				...Object.fromEntries(prepared.ids.map((id) => [id, prepared.price]))
			}))
			setSelected(new Set(prepared.ids))
			setSlider(prepared.ids.length)
		})
		return () => cancelAnimationFrame(frame)
	}, [props.prepared, ids, setSelected, setSlider])

	const floorPrice = React.useMemo(() => String(floorSats || ''), [floorSats])

	React.useEffect(() => {
		if (!globalPrice && floorPrice) {
			requestAnimationFrame(() => {
				setGlobalPrice(floorPrice)
			})
		}
	}, [floorPrice, globalPrice])

	// Pre-select inscription if passed via args
	React.useEffect(() => {
		if (args.inscriptionId && ids.includes(args.inscriptionId) && selected.size === 0) {
			requestAnimationFrame(() => {
				setSelected(new Set([args.inscriptionId]))
			})
		}
	}, [args.inscriptionId, ids, selected.size, setSelected])

	React.useEffect(() => {
		if (globalSelected && selected.size !== ids.length) {
			requestAnimationFrame(() => {
				setGlobalSelected(false)
			})
		}
	}, [globalSelected, ids.length, selected.size])

	//
	// Memos
	//

	const { selectedValue, totalPrice, totalPriceUsd, hasInvalidPrice } = React.useMemo(() => {
		let selectedValue = 0
		let totalPrice = 0
		let hasInvalidPrice = false

		// @ts-ignore
		for (let e of selected.entries()) {
			const id = e[0]
			const amount = parseFloat(data.find((a) => a.outpoint === id)?.amount)
			const rowPrice = parseFloat(String(prices[id] ?? ''))
			selectedValue += amount
			if (isNaN(rowPrice) || rowPrice <= 0) {
				hasInvalidPrice = true
				continue
			}
			totalPrice += rowPrice * amount
		}

		if (currency === NETWORK) {
			totalPrice = totalPrice * 1e8
		}

		const feePercentage = MARKET_FEE_RATE / 100
		const fee = Math.max(totalPrice * feePercentage, MARKET_FEE_MIN * selected.size)

		return {
			selectedValue,
			totalPrice: formatSatoshis(totalPrice),
			totalPriceUsd: formatNumber(((totalPrice / 1e8) * exchangeRate)?.toFixed(2)),
			fee: formatSatoshis(fee),
			feeUsd: formatUsdPer((fee / 1e8) * exchangeRate).usd,
			hasInvalidPrice
		}
	}, [currency, selected, exchangeRate, data, prices])

	//
	// Handlers
	//

	const applyGlobalPriceToAll = React.useCallback(
		(nextGlobal: string) => {
			if (!nextGlobal || ids.length === 0) {
				return
			}

			setSelected(new Set(ids))
			setSlider(ids.length)
			setGlobalSelected(true)
			setPrices(() => {
				const next: Record<string, string> = {}
				for (const id of ids) {
					next[id] = nextGlobal
				}
				return next
			})
		},
		[ids, setSelected, setSlider]
	)

	const clearGlobalSelection = React.useCallback(() => {
		setGlobalSelected(false)
		setSelected(new Set())
		setSlider(0)
		setPrices({})
	}, [setSelected, setSlider])

	const handleGlobalPriceChange = React.useCallback(
		(evt: any) => {
			const newGlobal = evt.target.value
			if (!newGlobal) {
				return
			}

			setGlobalPrice(newGlobal)
			applyGlobalPriceToAll(newGlobal)
		},
		[applyGlobalPriceToAll]
	)

	const handleGlobalPriceToggle = React.useCallback(() => {
		if (globalSelected) {
			clearGlobalSelection()
			return
		}

		const nextGlobal = globalPrice || floorPrice
		if (!nextGlobal) {
			return
		}

		setGlobalPrice(nextGlobal)
		applyGlobalPriceToAll(nextGlobal)
	}, [applyGlobalPriceToAll, clearGlobalSelection, floorPrice, globalPrice, globalSelected])

	const handleGlobalPriceFocus = React.useCallback(() => {
		const nextGlobal = globalPrice || floorPrice
		if (!nextGlobal) {
			return false
		}

		setGlobalPrice(nextGlobal)
		applyGlobalPriceToAll(nextGlobal)
	}, [applyGlobalPriceToAll, floorPrice, globalPrice])

	//
	// Renders
	//

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			return (
				<Item
					key={id}
					slug={slug}
					data={e}
					selected={selected.has(id)}
					onSelect={() =>
						// @ts-ignore
						featureFlags('app::disable_bulk') ? setSelected(new Set().add(id)) : onSelect(id)
					}
					price={prices[id] ?? ''}
					defaultPrice={prices[id] ?? (globalPrice || floorPrice)}
					onPriceChange={(evt) => setPrices((p) => ({ ...p, [id]: evt.target.value }))}
					step={step}
				/>
			)
		},
		[slug, selected, prices, globalPrice, floorPrice, step, setSelected, onSelect]
	)

	const hasTransferable = React.useMemo(() => {
		if (slug.startsWith('tap-') && data?.length) return true
		const id = parseFloat(token.transferable_balance)
		return !isNaN(id) && id !== 0
	}, [token, slug, data?.length])

	const snipeProtection = useSnipeProtection(
		protocolFromSlug(slug, { rune: isRune(slug), alkane: isAlkanes(slug) }),
		slug
	)

	const handleSubmit = React.useCallback(async () => {
		try {
			const ids: string[] = []
			const finalPrices: number[] = []

			// @ts-ignore
			for (const e of selected.entries()) {
				const id = e[0]
				const amount = parseFloat(data.find((o) => o.outpoint === id).amount)
				const unitPrice = parseFloat(String(prices[id] ?? ''))
				if (isNaN(unitPrice) || unitPrice <= 0) {
					throw new Error('Please set a valid price for each selected item')
				}
				ids.push(id)
				const final = parseInt((unitPrice * amount * (currency === NETWORK ? 1e8 : 1))?.toFixed(0))

				finalPrices.push(final)
			}

			const listingTarget = listingToastTarget(
				slug,
				ids.map((id) => data.find((row) => row.outpoint === id)).filter(Boolean)
			)

			// When protection handled part of the batch, only the rest goes the
			// standard way.
			const fallbackIds: string[] = []
			const fallbackPrices: number[] = []
			// One id for the whole attempt: the protected try, its rejection
			// and any standard listing it falls back to share it in telemetry.
			const attemptId = crypto.randomUUID()
			const protocol = protocolFromSlug(slug, { rune: isRune(slug), alkane: isAlkanes(slug) })
			let fellBack = false
			if (snipeProtection.enabled) {
				try {
					const result = await listSecureItems(
						ids.map((id, index) => {
							const row = data.find((o) => o.outpoint === id)
							return {
								inscriptionId:
									row?.inscription_id || (isRune(slug) || isAlkanes(slug) ? undefined : id),
								outpoint: row?.utxoOutpoint || row?.outpoint || id,
								priceSats: finalPrices[index],
								protocol,
								inscription: { ...row, collection: { slug } }
							}
						}),
						{ attemptId }
					)
					// Items protection refused only because of their shape (several
					// inscriptions on one UTXO, tiny postage, an unverified token
					// body) can still be listed the standard way. Do that for exactly
					// those, and say so; anything else keeps the failure.
					const legacyIds = result.failures
						.filter((failure) => LEGACY_LISTABLE_CODES.has(failure.code))
						.map((failure) => {
							const row = data.find(
								(o) => o.utxoOutpoint === failure.outpoint || o.outpoint === failure.outpoint
							)
							return row?.outpoint
						})
						.filter((id): id is string => !!id && ids.includes(id))
					if (legacyIds.length > 0 && legacyIds.length === result.failures.length) {
						if (result.listed > 0) {
							toastListingSuccess({
								title: 'Partially listed',
								description: `You listed ${result.listed} with snipe protection.`,
								count: result.listed,
								...listingTarget
							})
						}
						toast({
							title: 'Listing without snipe protection',
							description: result.failures[0].message
						})
						fellBack = legacyIds.length > 0
						legacyIds.forEach((id) => {
							fallbackIds.push(id)
							fallbackPrices.push(finalPrices[ids.indexOf(id)])
						})
					} else {
						const summary = describeSecureListingResult(result, listingNoun(classification))
						toastListingSuccess({
							title: summary.title,
							description: summary.description,
							...(summary.failed ? { variant: 'destructive' as const } : {}),
							isolate:
								isRune(slug) || isAlkanes(slug)
									? null
									: isolateTargetForFailures(
											result.failures,
											data.map((o) => ({
												inscriptionId: o.outpoint,
												outpoint: o.utxoOutpoint || o.outpoint
											})),
											slug
									  ),
							count: result.listed,
							...listingTarget
						})
						if (!summary.failed) onClose()
						return
					}
				} catch (e) {
					// A single-item listing throws its rejection instead of returning
					// it in `failures`. Two kinds still deserve a standard listing:
					// the wallet signed, but not the way protection needs (OKX signs
					// the key path), or the output's shape is one protection cannot
					// move (several inscriptions on one UTXO, tiny postage, an
					// unverified token body). Everything else is a real failure.
					const shapeRejected =
						e instanceof SecurePurchaseUnavailableError && LEGACY_LISTABLE_CODES.has(e.code)
					// Protection itself being unavailable (rollout/capabilities/cosigner
					// off, unsupported protocol or wallet) must never block a sale:
					// list the standard way, as before protection existed.
					const protectionUnavailable =
						e instanceof SecurePurchaseUnavailableError && SNIPE_UNAVAILABLE_CODES.has(e.code)
					if (!isProtectedSigningUnsupported(e) && !shapeRejected && !protectionUnavailable) throw e
					fellBack = true
					toast({
						title: 'Listing without snipe protection',
						description: protectionUnavailable
							? 'Snipe protection is unavailable right now, so this listing is being made the standard way.'
							: shapeRejected
							? secureListingFailureMessage((e as SecurePurchaseUnavailableError).code)
							: `${
									Wallet.display_name() || 'This wallet'
							  } cannot sign snipe-protected listings yet, so this listing is being made the standard way.`
					})
				}
			}

			const legacyIdList = fallbackIds.length > 0 ? fallbackIds : ids
			const legacyPriceList = fallbackIds.length > 0 ? fallbackPrices : finalPrices
			// After a protected rejection, the standard listing reports each
			// phase under the same attempt id, so the errors channel learns
			// whether the seller ended up listed. A plain standard listing
			// (protection off) stays untracked.
			const phase = <T,>(
				event: 'listing_build' | 'listing_sign' | 'listing_authorize',
				run: () => Promise<T>
			): Promise<T> =>
				fellBack ? trackStandardListingPhase(attemptId, protocol, event, run, slug) : run()

			const psbt = await phase('listing_build', () =>
				isRune(slug) || isAlkanes(slug)
					? Wallet.getRuneEscrows(legacyIdList, legacyPriceList)
					: Wallet.getEscrows(legacyIdList, legacyPriceList, {
							forceBulk: featureFlags('app::disable_bulk')
					  })
			)

			let totalCreatedEscrows = 0
			let totalSkippedInputs = 0
			let skippedInputs: any[] = []

			for (const e of Array.isArray(psbt) ? psbt : [psbt]) {
				const { rawtx } = await phase('listing_sign', () => Wallet.signEscrow(e))
				const summary = await phase('listing_authorize', async () => {
					const { data: resData } = await axios.post(`${API_URL}/market/escrow-bulk`, {
						psbt: rawtx
					})
					return Wallet.assertEscrowResponse(resData)
				})
				totalCreatedEscrows += summary.createdEscrows
				totalSkippedInputs += summary.skippedInputsCount
				skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
			}

			if (totalCreatedEscrows <= 0) {
				throw new Error('No escrows were created')
			}

			if (totalSkippedInputs > 0) {
				const skippedSummary = Wallet.formatEscrowSkips(skippedInputs)
				toastListingSuccess({
					title: 'Partially Listed',
					description: `Created ${formatNumber(
						totalCreatedEscrows
					)} escrows. Skipped ${formatNumber(totalSkippedInputs)} inputs${
						skippedSummary ? `: ${skippedSummary}` : '.'
					}.`,
					count: totalCreatedEscrows,
					...listingTarget
				})
			} else {
				toastListingSuccess({
					title: 'Success!',
					description: `You listed ${formatNumber(selectedValue)} ${listingNoun(classification)}`,
					count: ids.length,
					...listingTarget
				})
			}
			onClose()
		} catch (e: any) {
			toast({
				title: 'Error',
				description: e?.response?.data?.message || e?.message || 'Unable to list item',
				variant: 'destructive'
			})
		}
	}, [
		slug,
		onClose,
		selected,
		data,
		prices,
		currency,
		toast,
		selectedValue,
		classification,
		snipeProtection.enabled
	])

	const isDisabledForNoPrice = selected.size === 0 || hasInvalidPrice || totalPrice === '0'

	return (
		<>
			{!isFrozen && (
				<>
					<div className="flex px-2 h-7 w-full justify-between items-center">
						<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
						<div className="flex items-center gap-2">
							<p className="text-xxs text-muted-foreground">Set global price ({currency})</p>
							<SelectButton selected={globalSelected} onSelect={handleGlobalPriceToggle} />
							{isLoading ? (
								<div className="flex items-center justify-center h-7 w-24 rounded-md border border-input bg-background">
									<Loading size={14} />
								</div>
							) : (
								<Input
									value={globalPrice || floorPrice}
									step={currency === NETWORK ? step : 1}
									type="number"
									onClick={handleGlobalPriceFocus}
									onFocus={handleGlobalPriceFocus}
									onKeyDown={(evt) => evt.stopPropagation()}
									onChange={handleGlobalPriceChange}
									className={classNames(
										'text-xs py-0 px-2 h-7 w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
									)}
								/>
							)}
						</div>
					</div>
					<div className="flex px-2 py-1.5 gap-4">
						<Tabs value={tab} onValueChange={onChangeTab} slug={slug} />
						{!featureFlags('app::disable_bulk') && (
							<Slider
								defaultValue={[slider]}
								value={[slider]}
								max={data?.length}
								step={1}
								onValueChange={onSliderChange}
							/>
						)}
					</div>
				</>
			)}
			{(!isFungible(slug) || hasTransferable || isRune(slug) || isAlkanes(slug)) && (
				<>
					<div className="flex justify-between items-center px-2 py-3">
						<div className="flex gap-2 items-center">
							<SelectButton selected={allSelected} onSelect={onSelectAll} />
							<p className="text-xs text-muted-foreground select-none">
								Quantity ({selected.size > 0 ? `${selected.size}/` : ''}
								{data?.length} lots)
							</p>
						</div>
						<p className="text-xs text-muted-foreground select-none">Price ({currency})</p>
					</div>
			{props.marketActions}
					{isLoading && (
						<div className="flex items-center justify-center min-h-[120px]">
							<Loading size={16} />
						</div>
					)}
					{data?.map(renderOutpoint)}
					{!isLoading && data?.length === 0 && !isFungible(slug) && (
						<div className="px-2 py-8 text-center text-xs text-muted-foreground">
							<p>No unlisted items available.</p>
							<button type="button" className="mt-2 font-medium text-primary hover:underline" onClick={() => onChangeTab('delist')}>
								Manage listed items
							</button>
						</div>
					)}
				</>
			)}
			{isFungible(slug) && !hasTransferable && !isRune(slug) && !isAlkanes(slug) && (
				<p className="text-xs flex items-center justify-center h-full mt-8 gap-[2px]">
					You don't have any transferrable balance to list. Please
					<span
						className="text-violet-500 cursor-pointer"
						onClick={() => onChangeTab('split')}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => onChangeTab('split'))}
					>
						split
					</span>
					your balance.
				</p>
			)}
			<Invoice
				open={selected.size > 0}
				onSubmit={handleSubmit}
				// @ts-ignore
				disabled={isDisabledForNoPrice}
				action={
					isDisabledForNoPrice
						? 'Please set valid prices'
						: `List ${formatNumber(selectedValue)} ${classification}`
				}
				items={[
					{ title: 'You Receive', value: `${totalPrice} ${NETWORK} / $${totalPriceUsd}` },
					...snipeProtectionInvoiceItem(snipeProtection.offered)
				]}
			/>
		</>
	)
}

export default CollectionDetailList
